import { useDebouncedValue } from '@saaga/shared/hooks/use-debounced-value.hook'
import { useGetRecentlyUsedAgenciesQuery } from '@saaga/shared/store/agency-endpoints.store'
import { useGlobalSearchQuery } from '@saaga/shared/store/global-search-endpoints.store'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type {
  GlobalSearchResultGroup,
  GlobalSearchResultItem,
  GlobalSearchResultType,
} from './global-search.interface'
import { transformSearchApiResponse } from './global-search-api-transformer.util'
import { navigationItemList } from './global-search-data.util'
import { buildDefaultGroups } from './global-search-default-groups.util'
import { buildSearchResultGroups, getSearchResultPath } from './global-search-filters.util'

const SEARCH_DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 3
const RECENT_AGENCIES_PAGE_SIZE = 3

export interface AppLayoutSearchState {
  searchQuery: string
  searchResultGroupList: GlobalSearchResultGroup[]
  isSearching: boolean
}

export interface AppLayoutSearchHandlers {
  handleGlobalSearch: (query: string) => void
  handleResultSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
}

export interface UseAppLayoutSearchReturn {
  state: AppLayoutSearchState
  handlers: AppLayoutSearchHandlers
}

export const useAppLayoutSearch = (): UseAppLayoutSearchReturn => {
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)

  const { data: searchApiData, isFetching: isSearching } = useGlobalSearchQuery(debouncedQuery, {
    skip: debouncedQuery.length < MIN_QUERY_LENGTH,
  })

  const { data: recentAgenciesData } = useGetRecentlyUsedAgenciesQuery({
    pageSize: RECENT_AGENCIES_PAGE_SIZE,
  })

  const searchResultGroupList = useMemo(() => {
    if (!searchQuery) {
      return buildDefaultGroups(recentAgenciesData?.items ?? [], navigationItemList)
    }

    const staticGroupList = buildSearchResultGroups(searchQuery)
    const apiGroupList = searchApiData ? transformSearchApiResponse(searchApiData) : []
    return [...staticGroupList, ...apiGroupList]
  }, [searchQuery, searchApiData, recentAgenciesData])

  const handleGlobalSearch = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const handleResultSelect = useCallback(
    (type: GlobalSearchResultType, item: GlobalSearchResultItem) => {
      const path = getSearchResultPath(type, item)
      if (path) {
        navigate(`/${orgSlug}${path}`)
      }
    },
    [navigate, orgSlug]
  )

  return {
    state: { searchQuery, searchResultGroupList, isSearching },
    handlers: { handleGlobalSearch, handleResultSelect },
  }
}
